/**
 * Recipient Selector Component
 * Coláiste Lurgan Management System - Select students for email campaigns
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserPermissions } from '../../hooks/useAuth';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Users,
  Search,
  UserX,
  AlertCircle,
  CheckSquare,
  Square,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  Info
} from 'lucide-react';

import { useStudents, useStudentFormData } from '../../hooks/useStudents';
import { useMyFieldPermissions } from '../../hooks/usePermissions';
import StudentTable from '../students/StudentTable';
import FilterModal from '../students/FilterModalV2';
import api from '../../lib/api';

// Pagination Controls Component - Same as StudentsPage
const PaginationControls = ({
  currentPage,
  totalPages,
  pageSize,
  totalCount,
  onNextPage,
  onPreviousPage,
  onPageSizeChange,
  showPageSizeSelector = false,
  t
}) => {
  const pageSizeOptions = [25, 50, 100];
  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t bg-white dark:bg-background">
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-700 dark:text-gray-300">
          {t('pagination.showing', {
            from,
            to,
            total: totalCount
          })}
        </div>

        {showPageSizeSelector && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('pagination.show', 'Show')}:</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => onPageSizeChange(parseInt(value))}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('pagination.perPage', 'per page')}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPreviousPage}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          {t('pagination.previous')}
        </Button>

        <div className="text-sm text-gray-700 dark:text-gray-300">
          {t('pagination.pageOf', { current: currentPage, total: totalPages })}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={onNextPage}
          disabled={currentPage === totalPages}
        >
          {t('pagination.next')}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export function RecipientSelector({
  selectedRecipients = [],
  onRecipientsChange,
  onFiltersChange,
  sendToParents = false,
  campaignName = '' // Campaign name for filtering by sent status
}) {
  const { t } = useTranslation();

  // Get field permissions for filter visibility
  const { data: fieldPermissions } = useMyFieldPermissions();
  const filterPermissions = fieldPermissions?.filter_permissions || {};

  // Derive filter visibility from permissions
  const showInterviewFilter = filterPermissions.interview_score ?? false;
  const showPaymentFilter = filterPermissions.payment_fields ?? false;

  const searchDebounceRef = useRef(null);

  const { userType } = useUserPermissions();

  // State for filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [courseFilter, setCourseFilter] = useState('');
  const [cinnireFilter, setCinnireFilter] = useState('');
  const [notesFilter, setNotesFilter] = useState('');
  const [campaignSentFilter, setCampaignSentFilter] = useState('');
  const [interviewStageFilter, setInterviewStageFilter] = useState('');
  const [paymentStageFilter, setPaymentStageFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  // Notify parent of current filter params whenever filters change
  useEffect(() => {
    if (!onFiltersChange) return;
    onFiltersChange({
      status: statusFilter || undefined,
      course: courseFilter || undefined,
      is_cinnire: cinnireFilter || undefined,
      has_notes: notesFilter || undefined,
      interview_stage: interviewStageFilter || undefined,
      payment_stage: paymentStageFilter || undefined,
    });
  }, [statusFilter, courseFilter, cinnireFilter, notesFilter, interviewStageFilter, paymentStageFilter, onFiltersChange]);

  // Build campaign_sent parameter (format: "campaignName:status")
  const campaignSentParam = campaignName && campaignSentFilter
    ? `${campaignName}:${campaignSentFilter}`
    : undefined;

  // Use existing student hooks with pagination
  const {
    data: studentsResponse,
    isLoading: studentsLoading,
    error: studentsError
  } = useStudents({
    search: searchTerm,
    status: statusFilter || undefined,
    course: courseFilter || undefined,
    is_cinnire: cinnireFilter || undefined,
    has_notes: notesFilter || undefined,
    campaign_sent: campaignSentParam,
    campaign_name: campaignName || undefined, // Pass campaign name for email status
    interview_stage: interviewStageFilter || undefined,
    payment_stage: paymentStageFilter || undefined,
    page: currentPage,
    page_size: pageSize,
  });

  const { courses } = useStudentFormData();

  // Extract students and pagination from API response
  const students = studentsResponse?.results || [];
  const totalCount = studentsResponse?.count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Handle API errors - treat 500 as empty database (same as StudentsPage)
  const handleApiError = () => {
    // Check if this might just be an empty database (common in development)
    const isLikelyEmptyDatabase = studentsError?.response?.status === 500 ||
                                  studentsError?.response?.status === 404 ||
                                  studentsError?.message?.includes('No students found');

    if (isLikelyEmptyDatabase) {
      // Treat as successful but empty response
      return false; // Don't show error, show empty state instead
    }

    // For genuine errors (network, auth, etc.), show error
    return studentsError?.response?.status >= 400 && studentsError?.response?.status !== 404 && studentsError?.response?.status !== 500;
  };

  // Convert selectedRecipients array to Set for StudentTable
  const selectedStudentsSet = new Set(selectedRecipients);

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to page 1 when changing page size
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter && statusFilter !== 'ACTIVE') count++; // ACTIVE is default
    if (courseFilter) count++;
    if (cinnireFilter) count++;
    if (notesFilter) count++;
    if (campaignSentFilter) count++;
    if (interviewStageFilter) count++;
    if (paymentStageFilter) count++;
    return count;
  }, [statusFilter, courseFilter, cinnireFilter, notesFilter, campaignSentFilter, interviewStageFilter, paymentStageFilter]);

  // Get filter label
  const getFilterLabel = (type, value) => {
    if (!value) return null;

    switch (type) {
      case 'status':
        if (value === 'ACTIVE') return null; // Don't show default
        return t(`status.${value}`, value);
      case 'course':
        const course = courses?.find(c => c.uuid === value);
        if (!course) return value;
        return course.has_archived_students
          ? `${course.name} (${t('courses.archive.archivedBadge', 'Archived')})`
          : course.name;
      case 'cinnire':
        return value === 'true' ? t('recipients.cinniriOnly', 'Cinnirí Only') : t('recipients.nonCinniri', 'Non-Cinnirí');
      case 'campaign':
        return value === 'sent' ? t('recipients.alreadySent', 'Already Sent') : t('recipients.notSentYet', 'Not Sent Yet');
      case 'interview':
        if (value === 'scored') return t('filters.interview.scored', 'Scored');
        if (value === 'not_scored') return t('filters.interview.notScored', 'Not Scored');
        if (value.startsWith('score_')) {
          const score = value.split('_')[1];
          return `${t('filters.interview.score', 'Score')} ${score}`;
        }
        return value;
      case 'payment':
        const paymentLabels = {
          'fully_paid': t('filters.payment.fullyPaid', 'Fully Paid'),
          'outstanding': t('filters.payment.outstanding', 'Outstanding Balance'),
          'no_payments': t('filters.payment.noPayments', 'No Payments'),
          'refunded': t('filters.payment.refunded', 'Refunded'),
          'deposit_only': t('filters.payment.depositOnly', 'Deposit Only')
        };
        return paymentLabels[value] || value;
      default:
        return value;
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setStatusFilter('ACTIVE');
    setCourseFilter('');
    setCinnireFilter('');
    setNotesFilter('');
    setCampaignSentFilter('');
    setInterviewStageFilter('');
    setPaymentStageFilter('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Handle student selection (using UUIDs)
  const handleSelectStudent = (studentUuid) => {
    const newSelection = selectedRecipients.includes(studentUuid)
      ? selectedRecipients.filter(uuid => uuid !== studentUuid)
      : [...selectedRecipients, studentUuid];
    onRecipientsChange(newSelection);
  };

  // Handle select all on current page
  const handleSelectAll = () => {
    const allStudentUuids = students.map(s => s.uuid);
    const hasAllSelected = allStudentUuids.every(uuid => selectedRecipients.includes(uuid));

    if (hasAllSelected) {
      // Deselect all on this page
      const newSelection = selectedRecipients.filter(uuid => !allStudentUuids.includes(uuid));
      onRecipientsChange(newSelection);
    } else {
      // Select all on this page
      const newSelection = [...new Set([...selectedRecipients, ...allStudentUuids])];
      onRecipientsChange(newSelection);
    }
  };

  // Handle select all matching filters (all students across all pages)
  const handleSelectAllMatching = async () => {
    setIsSelectingAll(true);
    try {
      // Build the same filter params used for the student list
      const params = {
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        course: courseFilter || undefined,
        is_cinnire: cinnireFilter || undefined,
        campaign_sent: campaignSentParam,
        campaign_name: campaignName || undefined,
        interview_stage: interviewStageFilter || undefined,
        payment_stage: paymentStageFilter || undefined,
      };

      // Fetch all UUIDs matching current filters from API
      const response = await api.students.getUuids(params);
      const allUuids = response.uuids || [];

      // Add all UUIDs to selection (merge with existing)
      const newSelection = [...new Set([...selectedRecipients, ...allUuids])];
      onRecipientsChange(newSelection);

    } catch (error) {
      console.error('Failed to select all matching students:', error);
    } finally {
      setIsSelectingAll(false);
    }
  };

  const selectedCount = selectedRecipients.length;

  return (
    <div className="space-y-4">
      {/* Enhanced Summary Bar */}
      <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-950 dark:border-blue-800">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            <span className="text-lg font-semibold text-blue-900 dark:text-blue-100">
              {selectedCount} {t('recipients.selected', 'selected')}
            </span>
          </div>
          <div className="text-sm text-blue-700 dark:text-blue-300">
            {t('recipients.outOf', 'out of')} {totalCount} {t('recipients.matching', 'matching filters')}
          </div>
        </div>
        {selectedCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRecipientsChange([])}
            className="flex items-center gap-2"
          >
            <UserX className="h-4 w-4" />
            {t('recipients.clearAll', 'Clear All')}
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="flex gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('recipients.searchPlaceholder', 'Search students...')}
              defaultValue={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = setTimeout(() => setSearchTerm(value), 300);
              }}
              className="pl-10"
            />
          </div>

          {/* Filters Button */}
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setIsFilterModalOpen(true)}
          >
            <Filter className="h-4 w-4" />
            {t('recipients.filters', 'Filters')}
            {activeFilterCount > 0 && (
              <Badge variant="default" className="ml-1 px-1.5 py-0 text-xs h-5">
                {activeFilterCount}
              </Badge>
            )}
          </Button>

          {/* Results count */}
          <div className="flex items-center text-sm text-muted-foreground ml-auto">
            {studentsLoading ? (
              t('common.loading', 'Loading...')
            ) : (
              <>
                <span className="font-medium text-foreground">{totalCount}</span>
                <span className="ml-1">{t('recipients.results', 'results')}</span>
              </>
            )}
          </div>
        </div>

        {/* Active Filter Pills */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2">
            {getFilterLabel('status', statusFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('status', statusFilter)}
                <button
                  onClick={() => setStatusFilter('ACTIVE')}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {getFilterLabel('course', courseFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('course', courseFilter)}
                <button
                  onClick={() => setCourseFilter('')}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {getFilterLabel('cinnire', cinnireFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('cinnire', cinnireFilter)}
                <button
                  onClick={() => setCinnireFilter('')}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {getFilterLabel('campaign', campaignSentFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('campaign', campaignSentFilter)}
                <button
                  onClick={() => setCampaignSentFilter('')}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {getFilterLabel('interview', interviewStageFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('interview', interviewStageFilter)}
                <button
                  onClick={() => setInterviewStageFilter('')}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {getFilterLabel('payment', paymentStageFilter) && (
              <Badge variant="secondary" className="gap-1.5 pl-2 pr-1">
                {getFilterLabel('payment', paymentStageFilter)}
                <button
                  onClick={() => setPaymentStageFilter('')}
                  className="ml-0.5 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="h-6 px-2 text-xs"
            >
              {t('recipients.clearAll', 'Clear all')}
            </Button>
          </div>
        )}
      </div>

      {/* Bulk Selection Controls */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border dark:bg-gray-800">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              className="flex items-center gap-2"
            >
              {students.every(s => selectedRecipients.includes(s.uuid)) ? (
                <>
                  <Square className="h-4 w-4" />
                  {t('recipients.deselectPage', 'Deselect Page')}
                </>
              ) : (
                <>
                  <CheckSquare className="h-4 w-4" />
                  {t('recipients.selectPage', 'Select Page')}
                </>
              )}
            </Button>
          </div>

          {selectedCount > 0 && (
            <div className="text-sm text-muted-foreground">
              {selectedCount > students.length && (
                <span>{t('recipients.selectedAcrossPages', `${selectedCount} selected across multiple pages`)}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Gmail-style "Select All Matching" Banner */}
      {totalCount > students.length &&
       students.length > 0 &&
       students.every(s => selectedRecipients.includes(s.uuid)) && (
        <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              {t('recipients.allOnPageSelected', {
                defaultValue: `All ${students.length} students on this page are selected.`,
                count: students.length
              })}
            </span>
            <Button
              size="sm"
              variant="link"
              onClick={handleSelectAllMatching}
              disabled={isSelectingAll}
              className="text-blue-700 hover:text-blue-900 font-semibold dark:text-blue-300 dark:hover:text-blue-100"
            >
              {isSelectingAll
                ? t('recipients.selecting', 'Selecting...')
                : t('recipients.selectAllMatchingLink', {
                    defaultValue: `Select all ${totalCount} matching students`,
                    count: totalCount
                  })
              }
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Error Alert - Only show for genuine errors, not empty database */}
      {studentsError && handleApiError() && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t('common.error')}: {studentsError.message || 'There was an error loading the student data.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Top Pagination Controls */}
      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalCount={totalCount}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
          onPageSizeChange={handlePageSizeChange}
          showPageSizeSelector={true}
          t={t}
        />
      )}

      {/* Reuse existing StudentTable with selection mode */}
      <div className="border rounded-lg">
        <StudentTable
          students={students}
          isLoading={studentsLoading}
          selectedStudents={selectedStudentsSet}
          onSelectStudent={handleSelectStudent}
          onSelectAll={handleSelectAll}
          showSelection={true}
          showEmailStatus={!!campaignName}
          userPermissions={{ userType }}
          onEdit={() => {}} // Disable edit functionality
          onDelete={() => {}} // Disable delete functionality
          onView={() => {}} // Disable view functionality
        />
      </div>

      {/* Bottom Pagination Controls */}
      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalCount={totalCount}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
          onPageSizeChange={handlePageSizeChange}
          showPageSizeSelector={false}
          t={t}
        />
      )}

      {/* Filter Modal */}
      <FilterModal
        open={isFilterModalOpen}
        onOpenChange={setIsFilterModalOpen}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        courseFilter={courseFilter}
        setCourseFilter={setCourseFilter}
        cinnireFilter={cinnireFilter}
        setCinnireFilter={setCinnireFilter}
        notesFilter={notesFilter}
        setNotesFilter={setNotesFilter}
        uploadStatusFilter=""
        setUploadStatusFilter={() => {}}
        campaignSentFilter={campaignSentFilter}
        setCampaignSentFilter={setCampaignSentFilter}
        interviewStageFilter={interviewStageFilter}
        setInterviewStageFilter={setInterviewStageFilter}
        paymentStageFilter={paymentStageFilter}
        setPaymentStageFilter={setPaymentStageFilter}
        courses={courses}
        coursesLoading={false}
        activeFilterCount={activeFilterCount}
        onClearAll={clearAllFilters}
        showUploadStatusFilter={false}
        showCampaignSentFilter={!!campaignName}
        showInterviewStageFilter={showInterviewFilter}
        showPaymentStageFilter={showPaymentFilter}
        campaignName={campaignName}
      />
    </div>
  );
}

export default RecipientSelector;